import React from 'react';
import { AnyAction } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { closeOverlay, OverlayType, selectCartSummaryOverlayVisible } from 'store/slice/overlay';
import {
    LazyCartSummaryHeader,
    LazyCartSummaryListLoader,
    LazyCartSummaryScreen,
    LazyProductListItem,
    LazyCartSummaryFooter,
    LazyCartSummaryEmptyProductContent,
    LazyCartSummaryErrorContent,
    LazyCartPaginationError,
    LazyCartSummaryScrollView,
    LazyCartSlider,
} from 'core/utils/lazyCart';
import Sheet from 'component/common/KiteSheet/KiteSheet';
import { Box } from '@drone-js/core';
import { productOrchestrationActionCreators, connectFromCart, SLIDER_MAX_WIDTH, KITE_MODAL_ZINDEX } from '@cart-js/core';
import { routeTo } from 'store/slice/navigation';
import { getHomeUrl } from 'core/utils/activityUrlGenerator';
import { useNavigate } from 'react-router-dom';
import { resetState } from 'store/storeUtils';
import { selectBehaviorFromBootstrap, selectBrowserInfoFromBootstrap } from 'store/slice/bootstrap';
import { withErrorBoundary } from 'component/common/Hoc/ErrorBoundary';
import { selectIsRebrandCartToSaved } from 'core/utils/cart';

interface DispatchProps {
    getAndOrchestrateItems: () => void;
}

type Props = DispatchProps;

export const CartSummaryOverlayComponent: React.FC<Props> = React.memo((props: Props) => {
    const { getAndOrchestrateItems } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const behavior = useSelector(selectBehaviorFromBootstrap);
    const isVisible: boolean = useSelector(selectCartSummaryOverlayVisible);
    const { isMobile, isWebView } = useSelector(selectBrowserInfoFromBootstrap);
    const isRebrandCartToSaved = selectIsRebrandCartToSaved(behavior, isWebView);

    const onCloseOverlay = () => {
        dispatch(closeOverlay(OverlayType.CartSummary));
    };

    const onSearchTravelClicked = () => {
        onCloseOverlay();
        resetState(dispatch);
        const withHomeRootUrl = isWebView ? {} : { rootUrl: '' }; // if not WebView then redirect to root page, otherwise default behavior
        dispatch(routeTo({ navigate, path: getHomeUrl(withHomeRootUrl) }));
    };

    const cartSummary = (
        <React.Suspense fallback={null}>
            <LazyCartSummaryScreen isOpen={isVisible}>
                <LazyCartSummaryScrollView>
                    <LazyCartSummaryHeader onClose={onCloseOverlay} />
                    <LazyProductListItem />
                    <LazyCartSummaryEmptyProductContent onSearchForTravelButtonClick={onSearchTravelClicked} />
                    <LazyCartSummaryErrorContent onSearchForTravelButtonClick={onSearchTravelClicked} />
                    <LazyCartSummaryListLoader />
                    <LazyCartPaginationError />
                </LazyCartSummaryScrollView>
                <LazyCartSummaryFooter />
            </LazyCartSummaryScreen>
        </React.Suspense>
    );

    const oldCartSlider = (
        <Sheet
            onClose={onCloseOverlay}
            open={isVisible}
            data-element-name="cart-summary-overlay-panel"
            data-testid="cart-summary-overlay-panel"
            dock="right"
            showBackdrop={!isMobile}
            header={{ isHidden: true }}
            fill={isMobile ? 'full-screen' : 'content'}
            zIndex={KITE_MODAL_ZINDEX}
        >
            <Box width={!isMobile ? SLIDER_MAX_WIDTH : undefined} role="dialog" aria-modal={true}>
                {isVisible ? cartSummary : <div data-testid="cart-summary-overlay-hidden" />}
            </Box>
        </Sheet>
    );

    React.useEffect(() => {
        if (isVisible) {
            if (!isRebrandCartToSaved) {
                getAndOrchestrateItems();
            }
        }
    }, [isVisible, getAndOrchestrateItems, isRebrandCartToSaved]);

    return isRebrandCartToSaved ? (
        <React.Suspense fallback={null}>
            <LazyCartSlider isOpen={isVisible} handleOnClose={onCloseOverlay} onSearchForTravelButtonClick={onSearchTravelClicked} />
        </React.Suspense>
    ) : (
        oldCartSlider
    );
});

CartSummaryOverlayComponent.displayName = 'CartSummaryOverlayComponent';

const mapCartDispatchToProps = (dispatch: React.Dispatch<AnyAction>) => ({
    getAndOrchestrateItems: () => dispatch(productOrchestrationActionCreators.fetchCartItemThenOrchestrate() as unknown as AnyAction),
});

const CartSummaryOverlay = connectFromCart<{}, DispatchProps>(() => ({}), mapCartDispatchToProps)(withErrorBoundary(CartSummaryOverlayComponent));
CartSummaryOverlay.displayName = 'CartSummaryOverlay';

export default CartSummaryOverlay;
